import { useQuery } from '@apollo/client';
import { faChevronRight, faGear, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import Table from '../../../core/components/Table.tsx';
import TablePagination from '../../../core/components/TablePagination.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import useSearchParamState from '../../../core/utils/useSearchParamState.tsx';
import { canManageVoiceAgents } from '../../../users/utils.ts';
import Chip, { ChipVariant } from '../../assistants/components/config/Chip.tsx';
import { callDuration, callerLabel, OUTCOME_VARIANTS, SENTIMENT_VARIANTS } from '../callRecords.ts';
import CallDetailPanel from '../components/CallDetailPanel.tsx';
import { VOICE_AGENT_CONFIG_PATH, VOICE_AGENT_PATH, VOICE_AGENT_RECORD_PATH } from '../constants.ts';
import { GET_VOICE_AGENT, GET_VOICE_AGENT_RECORD, GET_VOICE_AGENT_RECORDS } from '../queries.ts';

const GearIcon = withIcon(faGear);
const ChevronRightIcon = withIcon(faChevronRight);
const WarningIcon = withIcon(faTriangleExclamation);

const COLUMN_COUNT = 7;
const PAGE_PARAM = 'page';
const ITEMS_PER_PAGE_PARAM = 'pageSize';
const DEFAULT_ITEMS_PER_PAGE = 10;

const ValueChip = ({
    value,
    variants,
    labelKey,
}: {
    value?: string | null;
    variants: Record<string, ChipVariant>;
    labelKey: string;
}) => {
    const { t } = useTranslation('voice_agents');
    if (!value) return <>—</>;
    return (
        <Chip variant={variants[value] || 'default'}>
            {t(`${labelKey}.${value}`, { defaultValue: value }) as string}
        </Chip>
    );
};

const CallRecordRow = ({ record, selected, onSelect }) => {
    const { t } = useTranslation('voice_agents');
    const documents = record.documents || [];
    const cellClass = classnames(
        'transition-colors duration-200 group-hover:bg-surface-medium',
        'group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-line-brand-strong',
        selected && '!bg-surface-brand-medium-default'
    );

    return (
        <Table.Row
            className="cursor-pointer focus-visible:outline-none"
            tabIndex={0}
            aria-current={selected || undefined}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelect();
            }}
        >
            <Table.Cell className={cellClass}>
                <span className="flex items-center gap-1.5 truncate font-medium text-content-strong">
                    {record.errorMessage && (
                        <WarningIcon className="text-content-danger-strong" title={record.errorMessage} />
                    )}
                    {callerLabel(record, t)}
                </span>
                {record.callerName && record.callerPhoneNumber && (
                    <span className="block truncate text-xs text-content-medium">{record.callerPhoneNumber}</span>
                )}
            </Table.Cell>
            <Table.Cell className={cellClass}>
                {documents.length > 0 ? t('history.documentsCreated', { count: documents.length }) : '—'}
            </Table.Cell>
            <Table.Cell className={classnames(cellClass, 'whitespace-nowrap')}>
                <ValueChip value={record.callOutcome} variants={OUTCOME_VARIANTS} labelKey="history.outcome" />
            </Table.Cell>
            <Table.Cell className={classnames(cellClass, 'whitespace-nowrap')}>
                <ValueChip value={record.sentiment} variants={SENTIMENT_VARIANTS} labelKey="history.sentiment" />
            </Table.Cell>
            <Table.Cell className={classnames(cellClass, 'whitespace-nowrap')}>{callDuration(record)}</Table.Cell>
            <Table.Cell className={classnames(cellClass, 'whitespace-nowrap')}>
                {format(new Date(record.createdAt), 'PPpp')}
            </Table.Cell>
            <Table.Cell className={classnames(cellClass, 'w-10 text-content-medium')}>
                <ChevronRightIcon />
            </Table.Cell>
        </Table.Row>
    );
};

const HistoryPage = () => {
    const { channelId, recordId } = useParams();
    const { t } = useTranslation('voice_agents');
    const { user } = useApplicationContext();
    const navigate = useNavigate();

    const [activePage, setActivePage] = useSearchParamState(PAGE_PARAM, '1');
    const [itemsPerPage, setItemsPerPage] = useSearchParamState(
        ITEMS_PER_PAGE_PARAM,
        DEFAULT_ITEMS_PER_PAGE.toString()
    );
    const parsedActivePage = parseInt(activePage, 10) || 1;
    const parsedItemsPerPage = parseInt(itemsPerPage, 10) || DEFAULT_ITEMS_PER_PAGE;

    const { data: agentData } = useQuery(GET_VOICE_AGENT, { variables: { id: channelId } });
    const agent = agentData?.voiceAgent;

    const { data, previousData, loading } = useQuery(GET_VOICE_AGENT_RECORDS, {
        variables: {
            voiceAgentId: channelId,
            offset: (parsedActivePage - 1) * parsedItemsPerPage,
            limit: parsedItemsPerPage,
        },
        fetchPolicy: 'cache-and-network',
    });
    // a page switch clears `data` until its request settles — keep the previous page on screen instead of flickering
    const recordsList = (data || previousData)?.voiceAgentRecordsList;
    const records = recordsList?.records || [];
    const totalCount = recordsList?.totalCount ?? 0;

    const { data: selectedData, loading: selectedLoading } = useQuery(GET_VOICE_AGENT_RECORD, {
        variables: { id: recordId },
        skip: !recordId,
    });
    const selectedRecord = selectedData?.voiceAgentRecord;

    // a page that no longer exists (page size raised, records deleted) would render empty. Only a settled
    // response may clamp: an in-flight one still reports the previous page's count.
    useEffect(() => {
        if (loading || !data) return;
        const lastPage = Math.max(1, Math.ceil(totalCount / parsedItemsPerPage));
        if (parsedActivePage > lastPage) setActivePage(lastPage.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data, totalCount, parsedItemsPerPage, parsedActivePage]);

    const openRecord = (id: string) =>
        navigate(url(VOICE_AGENT_RECORD_PATH, { channelId, recordId: id }, { keepSearch: true }));
    const closeRecord = () => navigate(url(VOICE_AGENT_PATH, { channelId }, { keepSearch: true }));

    return (
        <Layout>
            <Page>
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{agent?.name || t('dashboard.title')}</Page.HeaderTitle>
                    {canManageVoiceAgents(user) && agent && (
                        <Link to={url(VOICE_AGENT_CONFIG_PATH, { channelId: agent.id, tab: 'details' })}>
                            <Button>
                                <GearIcon /> {t('history.configure')}
                            </Button>
                        </Link>
                    )}
                </Page.Header>
                <div className="flex min-h-0 flex-1">
                    <Page.Content lowered className="flex min-w-0 flex-col gap-4 p-6">
                        <Table scrollable className="w-full table-auto">
                            <Table.Head>
                                <Table.Row>
                                    <Table.HeadCell>{t('history.table.caller')}</Table.HeadCell>
                                    <Table.HeadCell>{t('history.table.documents')}</Table.HeadCell>
                                    <Table.HeadCell>{t('history.table.outcome')}</Table.HeadCell>
                                    <Table.HeadCell>{t('history.table.sentiment')}</Table.HeadCell>
                                    <Table.HeadCell>{t('history.table.duration')}</Table.HeadCell>
                                    <Table.HeadCell>{t('history.table.createdAt')}</Table.HeadCell>
                                    <Table.HeadCell className="w-10" />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {records.length > 0 ? (
                                    records.map((record) => (
                                        <CallRecordRow
                                            key={record.id}
                                            record={record}
                                            selected={record.id === recordId}
                                            onSelect={() => openRecord(record.id)}
                                        />
                                    ))
                                ) : (
                                    <Table.Row>
                                        <Table.Cell colSpan={COLUMN_COUNT} className="text-center text-content-medium">
                                            {t('history.noCalls')}
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        </Table>
                        {totalCount > 0 && (
                            <TablePagination
                                totalCount={totalCount}
                                activePage={parsedActivePage}
                                itemsPerPage={parsedItemsPerPage}
                                setActivePage={(page) => setActivePage(page.toString())}
                                setItemsPerPage={(size) => {
                                    setItemsPerPage(size.toString());
                                    setActivePage('1');
                                }}
                            />
                        )}
                    </Page.Content>
                    {recordId && (
                        <CallDetailPanel record={selectedRecord} loading={selectedLoading} onClose={closeRecord} />
                    )}
                </div>
            </Page>
        </Layout>
    );
};

export default HistoryPage;
